import React, { useState, useEffect, useRef } from 'react';
import {
  PackageOpen,
  Plus,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConfirmModal from '../components/ConfirmModal';
import MyAnnouncementsCard from '../components/MyAnnouncementsCard';
import LoadingIndicator from '../components/LoadingIndicator';
import ProductPreviewModal, { ProductPreviewData } from '../components/ProductPreviewModal';
import UserSectionSidebar from '../components/UserSectionSidebar';
import { useTranslations } from '../translations';
import {
  deleteProduct,
  fetchOwnProductDetail,
  fetchUserProducts,
  updateProductStatus
} from '../services/product.api';
import { searchLocations } from '../services/location.api';
import { fetchCounties, forgetCachedCounties, isUnknownCountyError, readCachedCounties } from '../services/countyCache';
import { County, Location, UserProduct } from '../services/api.types';
import { getAccessToken } from '../services/auth';
import { useNotification } from '../components/NotificationProvider';
import { ASSETS } from '../constants/assets';
import { getPublishedAt } from '../utils/productDate';
import { formatPriceUnitType } from '../utils/unitType';
import { getPaginationItems } from '../utils/pagination';
import './MyAnnouncements.css';

interface MyAnnouncementsProps {
  isLoggedIn: boolean;
  isAuthChecking: boolean;
  isAdmin?: boolean;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onNavigateToHome: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToLocations?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToAdministration?: () => void;
  onLogout: () => void;
  onNavigateToCreateProduct?: () => void;
  onNavigateToProductDetail?: (productId: string) => void;
  onNavigateToEditProduct?: (productId: string) => void;
  onNavigateToMessages?: (productId?: string | null, searchTerm?: string) => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToCookies?: () => void;
}

const MY_ANNOUNCEMENTS_PAGE_SIZE = 15;
const priceNumberFormat = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 2 });

const MyAnnouncements: React.FC<MyAnnouncementsProps> = ({
  isLoggedIn,
  isAuthChecking,
  isAdmin = false,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onNavigateToProfile,
  onNavigateToLocations,
  onNavigateToSettings,
  onNavigateToAdministration,
  onLogout,
  onNavigateToCreateProduct,
  onNavigateToProductDetail,
  onNavigateToEditProduct,
  onNavigateToMessages,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies
}) => {
  const t = useTranslations();
  const [activeTab, setActiveTab] = useState<'DEMAND' | 'PRODUCT'>(() => (
    new URLSearchParams(window.location.search).get('productType') === 'DEMAND'
      ? 'DEMAND'
      : 'PRODUCT'
  ));
  const [filterOption, setFilterOption] = useState<'ACTIVE' | 'EXPIRED' | 'PENDING' | 'DECLINED'>('ACTIVE');
  const [nameFilter, setNameFilter] = useState('');
  const [appliedNameFilter, setAppliedNameFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [appliedLocationId, setAppliedLocationId] = useState('');
  const [counties, setCounties] = useState<County[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedCounty, setSelectedCounty] = useState<County | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [searchingWholeCounty, setSearchingWholeCounty] = useState(false);
  const [countySearchQuery, setCountySearchQuery] = useState('');
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [searchRevision, setSearchRevision] = useState(0);
  const [products, setProducts] = useState<UserProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [productToToggle, setProductToToggle] = useState<{ id: string; status: 'ACTIVE' | 'EXPIRED' } | null>(null);
  const [changingProductStatus, setChangingProductStatus] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);
  const [deletingProduct, setDeletingProduct] = useState(false);
  const [previewData, setPreviewData] = useState<ProductPreviewData | null>(null);
  const [previewLoadingProductId, setPreviewLoadingProductId] = useState<string | null>(null);
  const locationFilterRef = useRef<HTMLDivElement>(null);
  const locationRequestIdRef = useRef(0);
  const { showNotification } = useNotification();

  const handleOpenProduct = async (product: UserProduct) => {
    if (product.status === 'ACTIVE') {
      onNavigateToProductDetail?.(product.id);
      return;
    }

    if (previewLoadingProductId) {
      return;
    }

    try {
      setPreviewLoadingProductId(product.id);
      const productDetail = await fetchOwnProductDetail(product.id);
      setPreviewData({
        productType: productDetail.productType === 'DEMAND' ? 'BUY' : 'SELL',
        title: productDetail.title,
        description: productDetail.description,
        price: productDetail.price,
        images: productDetail.images
          .filter(({ imageUrl }) => Boolean(imageUrl?.trim()))
          .map(({ imageUrl }) => ({ preview: imageUrl })),
        categoryName: productDetail.category.name,
        subCategoryName: productDetail.subCategory?.name,
        locationName: productDetail.locationDto.diacriticsName || productDetail.locationDto.name,
        countyName: productDetail.locationDto.county.name,
        coordinates: productDetail.locationDto.coordinates,
        negotiable: productDetail.negotiable,
        userFirstName: productDetail.user.firstName,
        userLastName: productDetail.user.lastName,
        userImageUrl: productDetail.user.imageUrl,
        userPhone: productDetail.user.phoneNumber
      });
    } catch (error) {
      console.error('Error loading announcement preview:', error);
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      showNotification(t.myAnnouncements.errorLoading, 'error');
    } finally {
      setPreviewLoadingProductId(null);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, filterOption]);

  useEffect(() => {
    const handleOutsideInteraction = (event: MouseEvent | TouchEvent) => {
      if (
        locationFilterRef.current
        && !locationFilterRef.current.contains(event.target as Node)
      ) {
        setShowLocationDropdown(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowLocationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideInteraction);
    document.addEventListener('touchstart', handleOutsideInteraction);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction);
      document.removeEventListener('touchstart', handleOutsideInteraction);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    const query = locationFilter.trim();
    if (selectedLocationId || searchingWholeCounty || query.length < 3) {
      return;
    }

    const debounceTimer = window.setTimeout(async () => {
      const requestId = ++locationRequestIdRef.current;
      setLocationLoading(true);
      try {
        const response = await searchLocations(1, 20, null, query, getAccessToken());
        if (requestId !== locationRequestIdRef.current) {
          return;
        }
        setCounties(response.counties);
        setLocations(response.locations.content);
        setSelectedCounty(null);
        setShowLocationDropdown(true);
      } catch (error) {
        console.error('Error searching announcement locations:', error);
        if (requestId === locationRequestIdRef.current) {
          setCounties([]);
          setLocations([]);
        }
      } finally {
        if (requestId === locationRequestIdRef.current) {
          setLocationLoading(false);
        }
      }
    }, 300);

    return () => window.clearTimeout(debounceTimer);
  }, [locationFilter, selectedLocationId, searchingWholeCounty]);

  useEffect(() => {
    let cancelled = false;

    const loadProducts = async () => {
      if (isAuthChecking) {
        return;
      }
      const token = getAccessToken();

      if (!token) {
        onNavigateToLogin();
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await fetchUserProducts(
          activeTab,
          currentPage,
          MY_ANNOUNCEMENTS_PAGE_SIZE,
          filterOption,
          appliedNameFilter || null,
          appliedLocationId || null
        );
        if (cancelled) {
          return;
        }
        setProducts(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
      } catch (err) {
        console.error('Error fetching products:', err);
        const errorMessage = err instanceof Error ? err.message : '';
        if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
          onNavigateToLogin();
          return;
        }
        if (!cancelled) {
          setError(t.myAnnouncements.errorLoading);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProducts();

    return () => {
      cancelled = true;
    };
  }, [activeTab, filterOption, currentPage, appliedNameFilter, appliedLocationId, searchRevision, onNavigateToLogin, isAuthChecking, t.myAnnouncements.errorLoading]);

  const handleNameSearch = () => {
    setCurrentPage(1);
    setAppliedNameFilter(nameFilter.trim());
    setAppliedLocationId(selectedLocationId);
    setSearchRevision((revision) => revision + 1);
  };

  const handleClearNameSearch = () => {
    setNameFilter('');
    setCurrentPage(1);
    setAppliedNameFilter('');
    setSearchRevision((revision) => revision + 1);
  };

  const loadCounties = async () => {
    const requestId = ++locationRequestIdRef.current;
    const cachedCounties = readCachedCounties();
    if (cachedCounties) {
      setCounties(cachedCounties);
      setLocations([]);
      setSelectedCounty(null);
      setShowLocationDropdown(true);
      return;
    }

    setLocationLoading(true);
    setShowLocationDropdown(true);
    try {
      const loadedCounties = await fetchCounties(getAccessToken());
      if (requestId !== locationRequestIdRef.current) {
        return;
      }
      setCounties(loadedCounties);
      setLocations([]);
      setSelectedCounty(null);
    } catch (error) {
      console.error('Error loading announcement counties:', error);
      if (requestId === locationRequestIdRef.current) {
        setCounties([]);
      }
    } finally {
      if (requestId === locationRequestIdRef.current) {
        setLocationLoading(false);
      }
    }
  };

  const loadLocationsByCounty = async (county: County) => {
    const requestId = ++locationRequestIdRef.current;
    setLocationLoading(true);
    setSelectedCounty(county);
    try {
      const response = await searchLocations(1, 500, county.id, null, getAccessToken());
      if (requestId === locationRequestIdRef.current) {
        setLocations(response.locations.content);
      }
    } catch (error) {
      console.error('Error loading announcement county locations:', error);
      if (isUnknownCountyError(error)) {
        // The cached county list is from an older database: drop it and show the current one.
        forgetCachedCounties();
        if (requestId === locationRequestIdRef.current) {
          void loadCounties();
          return;
        }
      }
      if (requestId === locationRequestIdRef.current) {
        setLocations([]);
      }
    } finally {
      if (requestId === locationRequestIdRef.current) {
        setLocationLoading(false);
      }
    }
  };

  const handleLocationChange = (value: string) => {
    locationRequestIdRef.current += 1;
    setLocationFilter(value);
    setSelectedLocationId('');
    setSelectedLocation(null);
    setSelectedCounty(null);
    setSearchingWholeCounty(false);
    setCountySearchQuery('');
    setLocationSearchQuery('');

    if (!value) {
      setShowLocationDropdown(true);
      loadCounties();
    } else if (value.length < 3) {
      setShowLocationDropdown(false);
    }
  };

  const handleLocationSelect = (location: Location) => {
    setLocationFilter(`${location.diacriticsName}, ${location.county.name}`);
    setSelectedLocationId(location.id);
    setSelectedLocation(location);
    setShowLocationDropdown(false);
    setCountySearchQuery('');
    setLocationSearchQuery('');
    setSearchingWholeCounty(false);
  };

  const handleShowAllFromCounty = (county: County) => {
    setLocationFilter(county.name);
    setSelectedLocationId(county.id);
    setSelectedLocation(null);
    setSelectedCounty(county);
    setShowLocationDropdown(false);
    setCountySearchQuery('');
    setLocationSearchQuery('');
    setSearchingWholeCounty(true);
  };

  const handleClearLocationSearch = () => {
    locationRequestIdRef.current += 1;
    setLocationFilter('');
    setSelectedLocationId('');
    setAppliedLocationId('');
    setCounties([]);
    setLocations([]);
    setSelectedCounty(null);
    setSelectedLocation(null);
    setSearchingWholeCounty(false);
    setCountySearchQuery('');
    setLocationSearchQuery('');
    setShowLocationDropdown(false);
    setCurrentPage(1);
    setSearchRevision((revision) => revision + 1);
  };

  const filteredCounties = counties.filter((county) => (
    county.name.toLowerCase().includes(countySearchQuery.trim().toLowerCase())
  ));

  const filteredLocations = locations.filter((location) => {
    const query = locationSearchQuery.trim().toLowerCase();
    return location.name.toLowerCase().includes(query)
      || location.diacriticsName.toLowerCase().includes(query);
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const datePart = new Intl.DateTimeFormat('ro-RO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return t.myAnnouncements.postedOn
      .replace('{date}', datePart)
      .replace('{time}', `${hours}:${minutes}`);
  };

  const formatPrice = (price: { price: number; currency: string; quantity: number; unitType: string }) => {
    const unit = formatPriceUnitType(price.unitType, false);
    const currency = price.currency === 'RON' ? 'lei' : price.currency;
    const quantity = Number(price.quantity) > 1 ? `${price.quantity} ${unit}` : unit;
    return `${priceNumberFormat.format(price.price)} ${currency}/${quantity}`;
  };

  const currentStatusLabel = {
    ACTIVE: t.myAnnouncements.active,
    EXPIRED: t.myAnnouncements.expired,
    PENDING: t.myAnnouncements.pending,
    DECLINED: t.myAnnouncements.declined,
  }[filterOption];
  const announcementCountLabel = totalElements === 1
    ? t.myAnnouncements.oneAnnouncement
    : t.myAnnouncements.announcementCount.replace('{count}', totalElements.toString());

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggleStatusClick = (id: string, currentStatus: 'ACTIVE' | 'EXPIRED') => {
    setProductToToggle({ id, status: currentStatus });
  };

  const handleCancelStatusChange = () => {
    setProductToToggle(null);
  };

  const handleConfirmStatusChange = async () => {
    if (!productToToggle || changingProductStatus) return;

    setChangingProductStatus(true);
    try {
      const newStatus = productToToggle.status === 'ACTIVE' ? 'EXPIRED' : 'ACTIVE';
      await updateProductStatus(productToToggle.id, newStatus);

      showNotification(
        newStatus === 'ACTIVE' ? t.myAnnouncements.activateSuccess : t.myAnnouncements.deactivateSuccess,
        'success'
      );

      setProductToToggle(null);

      const token = getAccessToken();
      if (token) {
        const response = await fetchUserProducts(
          activeTab,
          currentPage,
          MY_ANNOUNCEMENTS_PAGE_SIZE,
          filterOption,
          appliedNameFilter || null,
          appliedLocationId || null
        );
        setProducts(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
      }
    } catch (err) {
      console.error('Error toggling product status:', err);
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      showNotification(t.myAnnouncements.statusChangeError, 'error');
    } finally {
      setChangingProductStatus(false);
    }
  };

  const handleDeleteClick = (id: string) => {
    setProductToDelete(id);
  };

  const handleCancelDelete = () => {
    setProductToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete || deletingProduct) return;

    setDeletingProduct(true);
    try {
      await deleteProduct(productToDelete);

      showNotification(t.myAnnouncements.deleteSuccess, 'success');

      setProductToDelete(null);

      const token = getAccessToken();
      if (token) {
        const response = await fetchUserProducts(
          activeTab,
          currentPage,
          MY_ANNOUNCEMENTS_PAGE_SIZE,
          filterOption,
          appliedNameFilter || null,
          appliedLocationId || null
        );
        setProducts(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      showNotification(t.myAnnouncements.deleteError, 'error');
    } finally {
      setDeletingProduct(false);
    }
  };

  const renderPagination = () => {
    const pages = getPaginationItems(currentPage, totalPages);
    const compactPages = getPaginationItems(currentPage, totalPages, true);

    const renderPages = (items: ReturnType<typeof getPaginationItems>) => items.map((page) => (
      typeof page !== 'number' ? (
        <span key={page} className="my-announcements-pagination-ellipsis">...</span>
      ) : (
        <button
          type="button"
          key={page}
          className={`my-announcements-pagination-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageChange(page)}
          aria-current={currentPage === page ? 'page' : undefined}
          aria-label={`Pagina ${page}`}
        >
          {page}
        </button>
      )
    ));

    return (
      <div className="my-announcements-pagination">
        <button
          type="button"
          className="my-announcements-pagination-button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Pagina anterioară"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14.0005 4.99993C14.2265 4.99993 14.4535 5.07593 14.6405 5.23193C15.0645 5.58493 15.1215 6.21593 14.7685 6.63993L10.2925 12.0109L14.6075 17.3729C14.9535 17.8029 14.8855 18.4329 14.4555 18.7789C14.0245 19.1249 13.3955 19.0569 13.0485 18.6269L8.22051 12.6269C7.92251 12.2559 7.92651 11.7259 8.23151 11.3599L13.2315 5.35993C13.4295 5.12293 13.7135 4.99993 14.0005 4.99993Z" fill="currentColor"/>
          </svg>
        </button>

        <div className="my-announcements-pagination-pages my-announcements-pagination-pages-desktop">
          {renderPages(pages)}
        </div>
        <div className="my-announcements-pagination-pages my-announcements-pagination-pages-mobile">
          {renderPages(compactPages)}
        </div>

        <button
          type="button"
          className="my-announcements-pagination-button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Pagina următoare"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.99951 19.0001C9.77351 19.0001 9.54651 18.9241 9.35951 18.7681C8.93551 18.4151 8.87851 17.7841 9.23151 17.3601L13.7075 11.9891L9.39251 6.62708C9.04651 6.19708 9.11451 5.56708 9.54451 5.22108C9.97551 4.87508 10.6045 4.94308 10.9515 5.37308L15.7795 11.3731C16.0775 11.7441 16.0735 12.2741 15.7685 12.6401L10.7685 18.6401C10.5705 18.8771 10.2865 19.0001 9.99951 19.0001Z" fill="currentColor"/>
          </svg>
        </button>
      </div>
    );
  };

  return (
    <div className="my-announcements-page">
      <Header
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={onNavigateToLocations}
        onMessagesClick={onNavigateToMessages}
        onAnnouncementsClick={() => {}}
        onAdministrationClick={onNavigateToAdministration}
        onSettingsClick={onNavigateToSettings}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
      />

      <div className="my-announcements-container">
        <UserSectionSidebar
          activeItem="announcements"
          onProfileClick={onNavigateToProfile}
          onLocationsClick={onNavigateToLocations}
          onMessagesClick={() => onNavigateToMessages?.()}
          onSettingsClick={onNavigateToSettings}
        />

        <main className="my-announcements-main">
          <section className="my-announcements-top-header" aria-labelledby="my-announcements-title">
            <div className="my-announcements-heading">
              <span className="my-announcements-eyebrow">
                <Sparkles size={15} aria-hidden="true" />
                {t.myAnnouncements.workspaceLabel}
              </span>
              <h1 id="my-announcements-title" className="my-announcements-title">{t.myAnnouncements.title}</h1>
              <p className="my-announcements-subtitle">{t.myAnnouncements.subtitle}</p>
            </div>
            <div className="my-announcements-hero-actions">
              <div className="my-announcements-overview" aria-label={`${currentStatusLabel}: ${announcementCountLabel}`}>
                <span>{currentStatusLabel}</span>
                <strong>{totalElements}</strong>
                <small>{activeTab === 'PRODUCT' ? t.myAnnouncements.tabSell : t.myAnnouncements.tabBuy}</small>
              </div>
              <button type="button" className="add-announcement-btn" onClick={onNavigateToCreateProduct}>
                <Plus size={18} strokeWidth={2.4} aria-hidden="true" />
                {t.myAnnouncements.addAnnouncement}
              </button>
            </div>
          </section>

          <section className="my-announcements-workspace" aria-label={t.myAnnouncements.filtersTitle}>
            <div className="my-announcements-workspace-heading">
              <div className="my-announcements-tabs" role="group" aria-label={t.myAnnouncements.announcementType}>
                <button
                  type="button"
                  className={`my-announcements-tab-button ${activeTab === 'PRODUCT' ? 'active' : ''}`}
                  onClick={() => setActiveTab('PRODUCT')}
                  aria-pressed={activeTab === 'PRODUCT'}
                >
                  {t.myAnnouncements.tabSell}
                </button>
                <button
                  type="button"
                  className={`my-announcements-tab-button ${activeTab === 'DEMAND' ? 'active' : ''}`}
                  onClick={() => setActiveTab('DEMAND')}
                  aria-pressed={activeTab === 'DEMAND'}
                >
                  {t.myAnnouncements.tabBuy}
                </button>
              </div>

              {/* All four statuses stay visible: owners switch between them often. */}
              <div className="my-announcements-status-filter" role="group" aria-label={t.myAnnouncements.statusFilterLabel}>
                {([
                  ['ACTIVE', t.myAnnouncements.active],
                  ['EXPIRED', t.myAnnouncements.expired],
                  ['PENDING', t.myAnnouncements.pending],
                  ['DECLINED', t.myAnnouncements.declined]
                ] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={filterOption === value}
                    className={`my-announcements-status-chip ${filterOption === value ? 'active' : ''}`}
                    onClick={() => setFilterOption(value)}
                  >
                    {label}
                    {filterOption === value && !loading && (
                      <span className="my-announcements-status-chip-count">{totalElements}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

          <form
            className="my-announcements-search"
            onSubmit={(event) => {
              event.preventDefault();
              handleNameSearch();
            }}
          >
            <div className="my-announcements-name-filter">
              <Search className="my-announcements-name-filter-icon" size={18} aria-hidden="true" />
              <input
                type="search"
                value={nameFilter}
                onChange={(event) => setNameFilter(event.target.value)}
                className="my-announcements-name-filter-input"
                placeholder={t.myAnnouncements.nameFilterPlaceholder}
                aria-label={t.myAnnouncements.nameFilterPlaceholder}
              />
              {nameFilter && (
                <button
                  type="button"
                  className="my-announcements-filter-icon-button"
                  onClick={handleClearNameSearch}
                  title={t.myAnnouncements.clearNameFilter}
                  aria-label={t.myAnnouncements.clearNameFilter}
                >
                  <X size={16} aria-hidden="true" />
                </button>
              )}
            </div>
            <div className="location-input-wrapper" ref={locationFilterRef}>
              <img src={ASSETS.PIN} alt="" className="location-pin-icon" />
              <input
                type="text"
                value={locationFilter}
                onChange={(event) => handleLocationChange(event.target.value)}
                onFocus={() => {
                  if (locationFilter.length < 3) {
                    if (counties.length > 0) {
                      setShowLocationDropdown(true);
                    } else {
                      loadCounties();
                    }
                  }
                }}
                className="location-input"
                placeholder={t.home.locationPlaceholder}
                aria-label={t.home.locationPlaceholder}
                autoComplete="off"
              />
              {locationFilter && (
                <button
                  type="button"
                  className="location-clear-button"
                  onClick={handleClearLocationSearch}
                  title={t.myAnnouncements.clearLocationFilter}
                  aria-label={t.myAnnouncements.clearLocationFilter}
                >
                  <X size={16} aria-hidden="true" />
                </button>
              )}
              <button
                type="button"
                className="location-dropdown-toggle"
                onClick={() => {
                  if (showLocationDropdown) {
                    setShowLocationDropdown(false);
                  } else if (counties.length > 0 || locations.length > 0) {
                    setShowLocationDropdown(true);
                  } else {
                    loadCounties();
                  }
                }}
                aria-label={t.myAnnouncements.toggleLocationFilter}
                title={t.myAnnouncements.toggleLocationFilter}
              >
                <svg
                  className={`location-dropdown-icon ${showLocationDropdown ? 'location-dropdown-icon-open' : ''}`}
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M11.9999 16C11.7719 16 11.5449 15.923 11.3599 15.768L5.35991 10.768C4.93591 10.415 4.87791 9.78399 5.23191 9.35999C5.58491 8.93599 6.21491 8.87899 6.63991 9.23199L12.0109 13.708L17.3729 9.39299C17.8029 9.04699 18.4329 9.11499 18.7789 9.54499C19.1249 9.97499 19.0569 10.604 18.6269 10.951L12.6269 15.779C12.4439 15.926 12.2219 16 11.9999 16Z" fill="#071105"/>
                </svg>
              </button>

              {showLocationDropdown && (
                <div className="location-dropdown">
                  {locationLoading ? (
                    <LoadingIndicator
                      message={t.common.loading}
                      variant="compact"
                      className="location-dropdown-loading"
                    />
                  ) : (
                    <>
                      {counties.length > 0 && !selectedCounty && (
                        <div className="location-dropdown-list">
                          <div className="location-dropdown-search">
                            <Search size={16} aria-hidden="true" />
                            <input
                              type="text"
                              placeholder={t.home.searchCounty}
                              value={countySearchQuery}
                              onChange={(event) => setCountySearchQuery(event.target.value)}
                              className="location-search-input"
                            />
                          </div>
                          {filteredCounties.map((county) => (
                            <button
                              key={county.id}
                              type="button"
                              className="location-dropdown-item county-item"
                              onClick={() => loadLocationsByCounty(county)}
                            >
                              {county.name}
                            </button>
                          ))}
                          {filteredCounties.length === 0 && (
                            <div className="location-dropdown-empty">{t.common.noResults}</div>
                          )}
                        </div>
                      )}

                      {selectedCounty && (
                        <>
                          <div className="location-dropdown-counties">
                            {counties.map((county) => (
                              <button
                                key={county.id}
                                type="button"
                                className={`location-dropdown-item county-item ${selectedCounty.id === county.id ? 'selected' : ''}`}
                                onClick={() => loadLocationsByCounty(county)}
                              >
                                {county.name}
                              </button>
                            ))}
                          </div>
                          <div className="location-dropdown-cities">
                            <div className="location-dropdown-header">
                              <button
                                type="button"
                                className="location-dropdown-show-all"
                                onClick={() => handleShowAllFromCounty(selectedCounty)}
                              >
                                {t.myAnnouncements.showAllFromCounty.replace('{county}', selectedCounty.name)}
                              </button>
                              <div className="location-dropdown-search">
                                <Search size={16} aria-hidden="true" />
                                <input
                                  type="text"
                                  placeholder={t.home.searchLocation}
                                  value={locationSearchQuery}
                                  onChange={(event) => setLocationSearchQuery(event.target.value)}
                                  className="location-search-input"
                                />
                              </div>
                            </div>
                            {filteredLocations.map((location) => (
                              <button
                                key={location.id}
                                type="button"
                                className="location-dropdown-item"
                                onClick={() => handleLocationSelect(location)}
                              >
                                {location.diacriticsName}
                              </button>
                            ))}
                            {filteredLocations.length === 0 && (
                              <div className="location-dropdown-empty">{t.common.noResults}</div>
                            )}
                          </div>
                        </>
                      )}

                      {!selectedCounty && locations.length > 0 && (
                        <div className="location-dropdown-list">
                          {locations.map((location) => (
                            <button
                              key={location.id}
                              type="button"
                              className="location-dropdown-item"
                              onClick={() => handleLocationSelect(location)}
                            >
                              {location.diacriticsName}, {location.county.name}
                            </button>
                          ))}
                        </div>
                      )}

                      {!locationLoading
                        && counties.length === 0
                        && locations.length === 0
                        && !selectedLocation
                        && !searchingWholeCounty
                        && locationFilter.length >= 3 && (
                          <div className="location-dropdown-empty">{t.common.noResults}</div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            <button type="submit" className="my-announcements-search-button">
              <Search size={17} aria-hidden="true" />
              {t.common.search}
            </button>
          </form>
          </section>

          <div className="my-announcements-results-heading">
            <h2>{t.myAnnouncements.resultsTitle}</h2>
            {!loading && <span aria-live="polite">{announcementCountLabel}</span>}
          </div>

          {loading ? (
            <LoadingIndicator message={t.common.loading} className="my-announcements-loading" />
          ) : error ? (
            <div className="my-announcements-error">{error}</div>
          ) : products.length === 0 ? (
            <div className="my-announcements-empty">
              <span className="my-announcements-state-icon"><PackageOpen size={28} aria-hidden="true" /></span>
              <strong>
                {appliedNameFilter || appliedLocationId
                  ? t.myAnnouncements.noFilterResults
                  : t.myAnnouncements.noAnnouncements}
              </strong>
            </div>
          ) : (
            <div className="my-announcements-list">
              {products.map((product) => (
                <MyAnnouncementsCard
                  key={product.id}
                  id={product.id}
                  title={product.title}
                  imageUrl={product.image?.thumbnailUrl || product.image?.imageUrl || ''}
                  location={`${product.locationDto.diacriticsName || product.locationDto.name}, ${product.locationDto.county.name}`}
                  datePosted={formatDate(getPublishedAt(product))}
                  views={product.views ?? 0}
                  price={formatPrice(product.price)}
                  negotiable={product.negotiable}
                  messagesCount={product.totalMessages || 0}
                  status={product.status as 'ACTIVE' | 'EXPIRED' | 'PENDING' | 'DECLINED'}
                  expirationDate={product.expirationDate}
                  onClick={() => void handleOpenProduct(product)}
                  onEdit={(id) => onNavigateToEditProduct?.(id)}
                  onToggleStatus={product.status === 'ACTIVE' || product.status === 'EXPIRED'
                    ? (id) => handleToggleStatusClick(id, product.status as 'ACTIVE' | 'EXPIRED')
                    : undefined}
                  onDelete={(id) => handleDeleteClick(id)}
                  onMessages={(id, title) => onNavigateToMessages?.(id, title)}
                />
              ))}
            </div>
          )}

          {totalPages > 1 && renderPagination()}
        </main>
      </div>

      {previewData && (
        <ProductPreviewModal
          previewData={previewData}
          onClose={() => setPreviewData(null)}
        />
      )}

      {productToToggle && (
        <ConfirmModal
          title={productToToggle.status === 'ACTIVE'
            ? t.myAnnouncements.deactivateModalTitle
            : t.myAnnouncements.activateModalTitle}
          message={productToToggle.status === 'ACTIVE'
            ? t.myAnnouncements.deactivateModalMessage
            : t.myAnnouncements.activateModalMessage}
          confirmText={productToToggle.status === 'ACTIVE'
            ? t.adminCard.deactivate
            : t.adminCard.activate}
          cancelText={t.common.cancel}
          destructive={productToToggle.status === 'ACTIVE'}
          isConfirming={changingProductStatus}
          onConfirm={() => void handleConfirmStatusChange()}
          onCancel={handleCancelStatusChange}
        />
      )}

      {productToDelete && (
        <ConfirmModal
          title={t.myAnnouncements.deleteModalTitle}
          message={t.myAnnouncements.deleteModalMessage}
          confirmText={t.common.delete}
          cancelText={t.common.cancel}
          destructive
          isConfirming={deletingProduct}
          onConfirm={() => void handleConfirmDelete()}
          onCancel={handleCancelDelete}
        />
      )}

      <Footer
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
      />
    </div>
  );
};

export default MyAnnouncements;
